import { Injectable, computed, effect, signal } from '@angular/core';
import { User } from '@models/user/user.model';
import { StorageService } from '@services/Logic/storage.service';

/**
 * UserStore service using Angular Signals for managing user state
 * This implements a global state pattern for user data using signals
 */
@Injectable({
  providedIn: 'root',
})
export class UserStore {
  // Private writable signal for internal state management
  private userState = signal<User | null>(null);

  // Public readonly computed signals for consuming components
  public readonly user = computed((): User => this.userState());
  public readonly isAuthenticated = computed(() => !!this.userState());
  public readonly fullName = computed(() => {
    const user = this.userState();
    return user ? `${user.firstName} ${user.lastName}` : '';
  });

  constructor(private storage: StorageService) {
    // Initialize from storage
    this.initFromStorage();

    // Set up effect to sync signal state to storage
    effect(() => {
      const currentUser = this.userState();
      if (currentUser) {
        this.syncToStorage(currentUser);
      }
    });
  }

  /**
   * Initialize the user state from storage
   */
  private initFromStorage() {
    const storageUser = this.storage.user;
    if (storageUser) {
      this.userState.set(storageUser);
    }
  }

  /**
   * Sync user data to storage
   */
  private syncToStorage(user: User) {
    if (!user) return;

    // Create a copy without the password before saving
    const userToStore = { ...user };
    delete userToStore.password;

    this.storage.user = userToStore;
  }

  /**
   * Update user with partial changes
   * @param changes Partial user object with changes
   * @returns The updated user
   */
  public updateUser(changes: Partial<User>): User | null {
    this.userState.update((currentUser: User): User => {
      if (!currentUser) return changes as User;
      return { ...currentUser, ...changes };
    });

    return this.user();
  }

  /**
   * Set entire user object
   * @param user The user object to set
   */
  public setUser(user: User | null) {
    this.userState.set(user);
  }

  /**
   * Clear user state (e.g., on logout)
   */
  public clearUser() {
    this.userState.set(null);
  }

  /**
   * Remove properties that haven't changed between two user objects
   * @param user Base user object
   * @param userChanges Changes to apply
   * @returns Object with only changed properties
   */
  public removeUnchangedProperties(
    user: User,
    userChanges: Partial<User>,
  ): Partial<User> {
    const tempUser = { ...userChanges };
    Object.keys(user).forEach((key) => {
      if (user[key] === tempUser[key]) delete tempUser[key];
    });
    return tempUser;
  }
}
